import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  ClipboardCheck, AlertTriangle, TestTube, MessageSquare, 
  TrendingUp, Users, Calendar, CheckCircle, Clock, Target,
  RefreshCw, Settings, AlertCircle, Activity, Eye, ArrowUpDown,
  UserCheck, FileText, Zap, Workflow
} from 'lucide-react';
import api from '../../api';

const Dashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [fonctionnelId, setFonctionnelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer l'ID fonctionnel 
  // useEffect(() => {
  //   const storedFonctionnelId = localStorage.getItem('utilisateur_id') || '6';
  //   setFonctionnelId(storedFonctionnelId);
  // }, []);

    useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setFonctionnelId(parseInt(user.id, 10));
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  // Récupérer les organisations
const fetchOrganisations = async () => {
  if (!fonctionnelId) return;

  try {
    // On utilise Axios → pas besoin de répéter l'URL complète
    const { data } = await api.get(`/organisations/fonctionnel/${fonctionnelId}`);

    setOrganisations(data.organisations || []);

    // Sélectionner la première organisation par défaut si aucune n’est sélectionnée
    if (data.organisations?.length > 0 && !selectedOrg) {
      setSelectedOrg(data.organisations[0].id.toString());
    }
  } catch (err) {
    console.error("Erreur organisations:", err);
  }
};

  // Récupérer les tickets
const fetchTickets = async () => {
  if (!selectedOrg || !fonctionnelId) return;

  setLoading(true);
  setError(null);

  try {
    // Promise.all avec Axios → exécuter les deux requêtes en parallèle
    const [clientRes, fonctionnelRes] = await Promise.all([
      api.get(`/tickets/client/${selectedOrg}`),
      api.get(`/tickets/fonctionel/${fonctionnelId}`),
    ]);

    const clientData = clientRes.data;
    const fonctionnelData = fonctionnelRes.data;

    // Combiner les données et supprimer les doublons
    const allTickets = [
      ...(clientData.tickets || []),
      ...(fonctionnelData.tickets || []),
    ];

    const uniqueTickets = allTickets.filter(
      (ticket, index, self) => index === self.findIndex((t) => t.id === ticket.id)
    );

    setTickets(uniqueTickets);
  } catch (err) {
    console.error("Erreur tickets:", err);
    setError("Erreur lors de la récupération des tickets");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (fonctionnelId) {
      fetchOrganisations();
    }
  }, [fonctionnelId]);

  useEffect(() => {
    if (selectedOrg && fonctionnelId) {
      fetchTickets();
    }
  }, [selectedOrg, fonctionnelId]);

  // MÉTRIQUES SPÉCIFIQUES AU CONSULTANT FONCTIONNEL

  // 1. Tickets à qualifier / en attente client
  const getTicketsAQualifier = () => {
    return tickets.filter(ticket => 
      ticket.status === 'nouveau' || 
      ticket.status === 'en_attente_qualification' ||
      ticket.etape_id === '1' || 
      ticket.etape_id === '2'
    );
  };

  const getTicketsEnAttenteClient = () => {
    return tickets.filter(ticket => 
      ticket.status === 'en_attente_client' || 
      ticket.status === 'attente_validation' ||
      ticket.validation_adminCl === '0'
    );
  };

  // 2. Statut précis à suivre
  const getStatutsPrecis = () => {
    const statusMap = {};
    tickets.forEach(ticket => {
      const key = `${ticket.status}-${ticket.etape_id}`;
      if (!statusMap[key]) {
        statusMap[key] = {
          status: ticket.status,
          etape_id: ticket.etape_id,
          count: 0,
          tickets: []
        };
      }
      statusMap[key].count++;
      statusMap[key].tickets.push(ticket);
    });
    return Object.values(statusMap);
  };

  // 3. Taux de tickets récurrents / similaires
  const getTicketsRecurrents = () => {
    const similarTickets = [];
    const contentMap = {};
    
    tickets.forEach(ticket => {
      const content = (ticket.content || '').toLowerCase();
      const nom = (ticket.nom || '').toLowerCase();
      const keywords = [...content.split(' '), ...nom.split(' ')]
        .filter(word => word.length > 4)
        .slice(0, 5); // Limiter pour éviter les performances
      
      keywords.forEach(keyword => {
        if (!contentMap[keyword]) {
          contentMap[keyword] = [];
        }
        contentMap[keyword].push(ticket);
      });
    });
    
    Object.values(contentMap).forEach(ticketGroup => {
      if (ticketGroup.length > 1) {
        ticketGroup.forEach(ticket => {
          if (!similarTickets.find(t => t.id === ticket.id)) {
            similarTickets.push(ticket);
          }
        });
      }
    });
    
    return similarTickets;
  };

  // 4. Alerte sur des problèmes non résolus
  const getProblenmesNonResolus = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    return tickets.filter(ticket => {
      const createdDate = new Date(ticket.created_at);
      return (
        createdDate < sevenDaysAgo &&
        ticket.status !== 'termine' &&
        ticket.status !== 'ferme' &&
        (ticket.type === 'bug' || ticket.priorite === '1' || ticket.priorite === '2')
      );
    });
  };

  // 5. Tickets en phase de test fonctionnel
  const getTicketsTestFonctionnel = () => {
    return tickets.filter(ticket => 
      ticket.status === 'test_fonctionnel' || 
      ticket.status === 'recette' ||
      ticket.status === 'pre_prod' ||
      ticket.etape_id === '4' ||
      ticket.etape_id === '5'
    );
  };

  // 6. Nombre de tickets avec échanges client
  const getTicketsAvecEchanges = () => {
    return tickets.filter(ticket => {
      const hasValidation = ticket.validation_adminCl === '1';
      const hasUpdates = ticket.updated_at && ticket.created_at && 
                        new Date(ticket.updated_at) > new Date(ticket.created_at);
      const hasClientInteraction = ticket.status === 'en_attente_client' || 
                                   ticket.status === 'attente_validation';
      
      return hasValidation || hasUpdates || hasClientInteraction;
    });
  };

  // 7. Nombre de tickets liés à des demandes d'évolution
  const getTicketsEvolution = () => {
    return tickets.filter(ticket => {
      const isEvolutionType = ticket.type === 'evolution';
      const hasEvolutionKeywords = ticket.content && (
        ticket.content.toLowerCase().includes('évolution') ||
        ticket.content.toLowerCase().includes('evolution') ||
        ticket.content.toLowerCase().includes('amélioration') ||
        ticket.content.toLowerCase().includes('workflow') ||
        ticket.content.toLowerCase().includes('nouveau') ||
        ticket.content.toLowerCase().includes('ajouter')
      );
      
      return isEvolutionType || hasEvolutionKeywords;
    });
  };

  // Calcul du taux de récurrence
  const getTauxRecurrence = () => {
    const recurrents = getTicketsRecurrents();
    return tickets.length > 0 ? ((recurrents.length / tickets.length) * 100).toFixed(1) : 0;
  };

  // Données pour les graphiques
  const statusFonctionnelData = [
    { name: 'À qualifier', value: getTicketsAQualifier().length, color: '#FF6B6B' },
    { name: 'En attente client', value: getTicketsEnAttenteClient().length, color: '#4ECDC4' },
    { name: 'Test fonctionnel', value: getTicketsTestFonctionnel().length, color: '#45B7D1' },
    { name: 'Avec échanges', value: getTicketsAvecEchanges().length, color: '#96CEB4' },
    { name: 'Évolutions', value: getTicketsEvolution().length, color: '#FFEAA7' }
  ];

  const statutsDetailData = getStatutsPrecis().map(item => ({
    name: `${item.status} (${item.etape_id})`,
    value: item.count,
    color: item.status === 'pret' ? '#27AE60' : 
           item.status === 'nouveau' ? '#E74C3C' : 
           item.status === 'en_attente_client' ? '#F39C12' : 
           item.status === 'test_fonctionnel' ? '#3498DB' : '#95A5A6'
  }));

  // Évolution temporelle des tickets
  const getEvolutionTemporelle = () => {
    const monthlyData = {};
    
    tickets.forEach(ticket => {
      const month = new Date(ticket.created_at).toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'short' 
      });
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          total: 0,
          evolutions: 0,
          tests: 0,
          echanges: 0
        };
      }
      
      monthlyData[month].total++;
      
      if (getTicketsEvolution().find(t => t.id === ticket.id)) {
        monthlyData[month].evolutions++;
      }
      
      if (getTicketsTestFonctionnel().find(t => t.id === ticket.id)) {
        monthlyData[month].tests++;
      }
      
      if (getTicketsAvecEchanges().find(t => t.id === ticket.id)) {
        monthlyData[month].echanges++;
      }
    });
    
    return Object.values(monthlyData).sort((a, b) => 
      new Date(a.month) - new Date(b.month)
    );
  };

  const MetricCard = ({ title, value, icon: Icon, color, subtitle, alert = false, trend = null }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${alert ? 'border-red-500' : ''}`} 
         style={{ borderLeftColor: alert ? '#E74C3C' : color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold ${alert ? 'text-red-600' : 'text-gray-900'}`}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <p className="text-xs mt-1" style={{ color: trend > 0 ? '#27AE60' : '#E74C3C' }}>
              {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
            </p>
          )}
        </div>
        <div className="p-3 rounded-full" style={{ backgroundColor: (alert ? '#E74C3C' : color) + '20' }}>
          <Icon size={24} style={{ color: alert ? '#E74C3C' : color }} />
        </div>
      </div>
    </div>
  );

  const AlertCard = ({ tickets, title, color, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center mb-2">
        <Icon size={20} style={{ color }} className="mr-2" />
        <h4 className="font-semibold text-gray-800">{title}</h4>
      </div>
      <div className="space-y-2">
        {tickets.slice(0, 3).map(ticket => (
          <div key={ticket.id} className="text-sm text-gray-600 border-b pb-1">
            <span className="font-medium">{ticket.nom}</span>
            <div className="flex gap-2 mt-1">
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {ticket.type}
              </span>
              <span className="text-xs bg-blue-100 px-2 py-1 rounded">
                Priorité {ticket.priorite}
              </span>
              <span className="text-xs bg-green-100 px-2 py-1 rounded">
                {ticket.status}
              </span>
            </div>
          </div>
        ))}
        {tickets.length > 3 && (
          <p className="text-xs text-gray-500 mt-2">+{tickets.length - 3} autres tickets</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-2 text-lg">Chargement des données...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">Erreur: {error}</span>
        </div>
        <button 
          onClick={fetchTickets}
          className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tableau de Bord - Consultant Fonctionnel
          </h1>
          <p className="text-gray-600">
            Gestion de la qualification des besoins, tests et échanges clients
          </p>
        </div>

        {/* Contrôles */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Settings size={20} className="text-gray-500" />
              <label className="text-sm font-medium text-gray-700">Organisation:</label>
              <select 
                value={selectedOrg} 
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="">Sélectionner une organisation</option>
                {organisations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.nomOrganisation}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <UserCheck size={20} className="text-gray-500" />
              <label className="text-sm font-medium text-gray-700">Fonctionnel ID:</label>
              <input 
                type="text" 
                value={fonctionnelId} 
                onChange={(e) => {
                  setFonctionnelId(e.target.value);
                }}
                className="border rounded px-3 py-1 text-sm w-20"
                placeholder="ID"
              />
            </div>
            <button 
              onClick={fetchTickets}
              className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              <RefreshCw size={16} />
              Actualiser
            </button>
            <div className="ml-auto text-sm text-gray-500">
              Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
            </div>
          </div>
        </div>

        {/* Métriques principales - Consultant Fonctionnel */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Tickets à qualifier"
            value={getTicketsAQualifier().length}
            icon={ClipboardCheck}
            color="#FF6B6B"
            subtitle="Besoins à analyser"
            alert={getTicketsAQualifier().length > 5}
          />
          <MetricCard
            title="En attente client"
            value={getTicketsEnAttenteClient().length}
            icon={Clock}
            color="#4ECDC4"
            subtitle="Validation client requise"
            alert={getTicketsEnAttenteClient().length > 3}
          />
          <MetricCard
            title="Test fonctionnel"
            value={getTicketsTestFonctionnel().length}
            icon={TestTube}
            color="#45B7D1"
            subtitle="Recette / Pré-production"
          />
          <MetricCard
            title="Avec échanges client"
            value={getTicketsAvecEchanges().length}
            icon={MessageSquare}
            color="#96CEB4"
            subtitle="Communication active"
          />
        </div>

        {/* Métriques secondaires */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Demandes d'évolution"
            value={getTicketsEvolution().length}
            icon={TrendingUp}
            color="#FFEAA7"
            subtitle="Besoins métiers"
          />
          <MetricCard
            title="Tickets récurrents"
            value={getTicketsRecurrents().length}
            icon={AlertTriangle}
            color="#F39C12"
            subtitle={`Taux: ${getTauxRecurrence()}%`}
            alert={parseFloat(getTauxRecurrence()) > 20}
          />
          <MetricCard
            title="Problèmes non résolus"
            value={getProblenmesNonResolus().length}
            icon={AlertCircle}
            color="#E74C3C"
            subtitle="> 7 jours"
            alert={getProblenmesNonResolus().length > 0}
          />
        </div>

        {/* Alertes et actions requises */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {getTicketsAQualifier().length > 0 && (
            <AlertCard 
              tickets={getTicketsAQualifier()} 
              title="Tickets à qualifier"
              color="#FF6B6B"
              icon={ClipboardCheck}
            />
          )}
          {getProblenmesNonResolus().length > 0 && (
            <AlertCard 
              tickets={getProblenmesNonResolus()} 
              title="Problèmes non résolus"
              color="#E74C3C"
              icon={AlertCircle}
            />
          )}
          {getTicketsRecurrents().length > 0 && (
            <AlertCard 
              tickets={getTicketsRecurrents()} 
              title="Tickets récurrents"
              color="#F39C12"
              icon={AlertTriangle}
            />
          )}
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Vue d'ensemble fonctionnelle</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusFonctionnelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusFonctionnelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Statuts détaillés</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statutsDetailData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#45B7D1">
                  {statutsDetailData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Évolution temporelle */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Évolution des activités fonctionnelles</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={getEvolutionTemporelle()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="total" stroke="#45B7D1" strokeWidth={2} name="Total" />
              <Line type="monotone" dataKey="evolutions" stroke="#FFEAA7" strokeWidth={2} name="Évolutions" />
              <Line type="monotone" dataKey="tests" stroke="#96CEB4" strokeWidth={2} name="Tests" />
              <Line type="monotone" dataKey="echanges" stroke="#4ECDC4" strokeWidth={2} name="Échanges" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Informations système */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Target className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-blue-700 font-medium">
                Total des tickets: {tickets.length}
              </span>
            </div>
            <div className="text-blue-600 text-sm">
              Organisation: {organisations.find(o => o.id.toString() === selectedOrg)?.nomOrganisation || 'Non sélectionnée'} | 
              Fonctionnel: {fonctionnelId}
            </div>
          </div>
          <div className="mt-2 text-blue-600 text-sm">
            <p>Taux de récurrence: {getTauxRecurrence()}% | 
               Tickets en test: {getTicketsTestFonctionnel().length} | 
               Demandes d'évolution: {getTicketsEvolution().length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;